// import { ToastrService } from 'ngx-toastr';
import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { environment as env } from 'src/environments/environment';

import { UserData, UserGroupKey } from '../models/user.data-object';
import { Constants } from '../models/constants';

import { ToastrService } from 'ngx-toastr';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';

@Injectable({
  providedIn: 'root'
})
export class AppCoreService {

  isUserLoggedIn = new BehaviorSubject<boolean>(this.checkUserData());
  private selectedServiceCenterId = new BehaviorSubject<number>(this.GetServiceCenter());
  private creditDetails$ = new BehaviorSubject<{ limit: number, balance: number, used: number }>({ limit: 0, balance: 0, used: 0 });
  public reconciledAsset: any;
  private userDataSubject = new BehaviorSubject<any>(JSON.parse(localStorage.getItem('ud') || '{}'));

  private $shortcut = new BehaviorSubject<{ content: string; title?: string }>({ content: '' });

  /** ✅ NEW — Global Edit Mode Tracker */
  public isEditMode$ = new BehaviorSubject<boolean>(false);
  private sessionSocket: WebSocket | null = null;

  constructor(private Global: Constants,
    private toastr: ToastrService,
    private router: Router,
    private ngxLoaderService: NgxUiLoaderService
  ) {
    this.detectUrl();
    this.trackEditMode();
  };

  /** Existing loader + shortcut logic */
  private detectUrl() {
    let previousUrl: string | null = null;

    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.ngxLoaderService.start();
      }

      if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        const currentUrl = event instanceof NavigationEnd ? event.urlAfterRedirects : null;

        if (previousUrl === '/account/login' && currentUrl === '/home') {
          setTimeout(() => {
            this.ngxLoaderService.stop();
          }, 1000);
        } else {
          this.ngxLoaderService.stop();
        }

        if (event instanceof NavigationEnd) {
          this.resetShortcutOnURLChange(currentUrl);
          previousUrl = currentUrl;
        }
      }
    });
  };

  private resetShortcutOnURLChange(url: string) {
    if (!url.includes('/operation/awb')) {
      this.updateShortcut('');
    }
  }

  /** ✅ NEW — Global Edit Mode Logic */
  private trackEditMode() {
    // Initial check for direct-load (like refresh or new tab)
    this.checkEditMode(this.router.url);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.checkEditMode(event.urlAfterRedirects);
      });
  }

  private checkEditMode(url: string) {
    // add your patterns here as needed
    const editPatterns = [/\/edit\//, /\/inbound/, /\/update\//];
    const isEdit = editPatterns.some(p => p.test(url));
    this.isEditMode$.next(isEdit);
  }

  // Method to update shortcut visibility and content dynamically
  updateShortcut(content: string, title?: string) {
    this.$shortcut.next({ content, title });
  }


  getShortcutObservable(): Observable<{ content: string; title?: string }> {
    return this.$shortcut.asObservable();
  }

  // Set credit details only if there's a change
  public setCreditDetails(creditDetails: { limit: number, balance: number, used: number }) {
    const currentDetails = this.creditDetails$.getValue();

    // Check if the new values are different
    if (
      currentDetails.limit !== creditDetails.limit ||
      currentDetails.balance !== creditDetails.balance ||
      currentDetails.used !== creditDetails.used
    ) {
      this.creditDetails$.next(creditDetails);
    }
  }


  //get credit details
  public getCurrentCreditDetails(): Observable<{ limit: number, balance: number, used: number }> {
    return this.creditDetails$.asObservable();
  }
  // abstract methods for local storage
  public getDataFromLS(key): any {
    return localStorage.getItem(key);
  }

  public setDataInLS(key, value): void {
    localStorage.setItem(key, value);
  }

  public removeDataFromLS(key): void {
    localStorage.removeItem(key);
  }

  public removeAllFromLS(): void {
    localStorage.clear();
  }

  // User section - start
  private checkUserData(): boolean {
    const saved_token = !!localStorage.getItem('ud');
    if (saved_token) {
      const userData: UserData = JSON.parse(localStorage.getItem('ud'));
      return userData.isUserLoggedIn;
    }
    return false;
  }

  public CheckUserLoggedIn(): Observable<boolean> {
    return this.isUserLoggedIn.asObservable();
  }

  public GetUserData(): UserData {
    const userData: UserData = JSON.parse(localStorage.getItem('ud'));
    return userData;
  }

  public getUserDataChanges(): Observable<any> {
    return this.userDataSubject.asObservable();
  }

  public SetUserData(value: any, loginRedirect = true): void {
    localStorage.setItem('ud', JSON.stringify(value));
    this.userDataSubject.next(value);
    if (loginRedirect) {
      this.isUserLoggedIn.next(true);
    }
  }

  public RemoveUserData(): void {
    localStorage.removeItem('ud');
    this.isUserLoggedIn.next(false);
  }

  // User section - ends

  private GetServiceCenter(): number {
    const saved_token = !!localStorage.getItem('ud');
    if (saved_token) {
      const userData: UserData = JSON.parse(localStorage.getItem('ud'));
      return userData.selectedServiceCenter;
    }
    return 0;
  }

  public SetServiceCenter(value: number): void {
    const saved_token = !!localStorage.getItem('ud');
    if (saved_token) {
      const userData: UserData = JSON.parse(localStorage.getItem('ud'));
      userData.selectedServiceCenter = value;
      localStorage.setItem('ud', JSON.stringify(userData));
      this.selectedServiceCenterId.next(value);
    } else {
      this.selectedServiceCenterId.next(0);
    }
  }

  public SelectedServiceCenter(): Observable<number> {
    return this.selectedServiceCenterId.asObservable();
  }



  /**
   * @description Check token value and validity and User flag
   * then return user validity
   * as of now isTokenValid: true, once tokenizaition will be enabled, the default value will be false
   * @returns user validity: true
   */
  isUserAuthenticated(): boolean {
    let isTokenValid = true;
    const isTokenEnable = env.apiTokenizationEnabled ? true : false;

    if (isTokenEnable) {
      // TODO:
      isTokenValid = true;
    }
    return this.getUserLoggedInStatus() && isTokenValid;
  }

  getUserLoggedInStatus(): boolean {
    return this.GetUserData() === null ? false : this.GetUserData().isUserLoggedIn;
  }

  // toastr messages
  showMessage(message, title = '', type = 'success'): void {
    if (type === 'error') {
      this.toastr.error(message, title);
    } else if (type === 'warning') {
      this.toastr.warning(message, title);
    } else if (type === 'info') {
      this.toastr.info(message, title);
    } else {
      this.toastr.success(message, title);
    }
  }

  setCheckConfig(value: any) {
    const saved_token = !!localStorage.getItem('ud');
    if (saved_token) {
      const userData: UserData = JSON.parse(localStorage.getItem('ud'));
      userData.metaInfo.formconfig = value;
      localStorage.setItem('ud', JSON.stringify(userData));
    }
  }

  get isCustomerGroup(): boolean {
    const user = this.GetUserData();
    return (
        user?.groupkey === UserGroupKey.CUSTOMERADMIN ||
        user?.groupkey === UserGroupKey.SHIPPERADMIN
    );
  }

  startSessionSocket() {
    const user: any = this.GetUserData();
    if (!user?.sessionid || !user?.userId) return;

    const api = env.apiUrl.replace(/\/local\/api.*/, '').replace(/\/api.*/, '');
    // console.log('api--', api);

    const wsUrl = api.replace('http', 'ws') + '/session';
    // console.log('WS URL:', wsUrl);

    this.sessionSocket = new WebSocket(wsUrl);
    this.sessionSocket.onopen = () => {
      this.sessionSocket!.send(JSON.stringify({
        sessionid: user.sessionid,
        userid: user.userId
      }));
    };
    // console.log('session--', this.sessionSocket);

    this.sessionSocket.onmessage = (e) => {
      const msg = JSON.parse(e.data);

      if (msg.type === 'FORCE_LOGOUT') {
        this.forceLogout("Your access was revoked by admin");
      }
    };

    this.sessionSocket.onclose = () => {
      // console.log('Session socket closed');
    };
  }

  forceLogout(reason = 'Session expired') {
    this.sessionSocket?.close();
    this.sessionSocket = null;

    localStorage.clear();
    this.isUserLoggedIn.next(false);

    this.router.navigate(['/account/login'], {
      queryParams: { reason }
    });
  }


}
